import React, {useState} from 'react'
import {observer} from 'mobx-react-lite'
import {FormattedNumber} from 'react-intl'
import {Box, BoxProps, Button, Flex, Stack, Text} from '../../../vanilla'
import {BaseButton} from '../../../vanilla'
import {Link} from '../../../components/link'
import {useBasketStore, useOrderStore} from '../../../store/hooks/useStore'
import {CollapsablePanel} from '../../../components/collapsable-panel'
import {OrderSummary} from '../../../components/order-summary'
import {ChevronDownIcon} from '../../../components/icons'
import {OrderSummaryProgressBars} from '../../../components/order-summary/progress-bars'
import {useExitEditModeModal} from '../use-exit-order-edit-mode-context'
import {isMobile} from '../../../utils/utils'
import { BasketToCheckoutButton } from './basket-to-checkout-button'

interface CartSummaryAccordionProps extends BoxProps<'div'> {
  checkoutButtonText: string,
  handleCheckoutButtonClick: () => void
  includeBag?: boolean
  setIncludeBag?: (value: boolean) => void
}

export const CartSummaryAccordion = observer((props: CartSummaryAccordionProps) => {
  const {
    checkoutButtonText,
    handleCheckoutButtonClick,
    includeBag,
    setIncludeBag,
  } = props
  const exitEditModeModal = useExitEditModeModal()
  const [isOpen, setIsOpen] = useState(false)

  const {editMode, orderEditProductItemsDelta} = useOrderStore()
  const {
    basket
  } = useBasketStore()

  const showEditOrderMessage =
    editMode &&
    (Object.values(orderEditProductItemsDelta).find(({recentlyAdded}) => recentlyAdded) || false)

  // Check screen size to display correct amount to customer
  const isMobileFlag = isMobile()

  return (
    <Stack
      align="flex-start"
      position="fixed"
      bottom="0px"
      width="full"
      bg="white"
      zIndex="sticky"
      {...(props || {})}
    >
      <CollapsablePanel
        width="full"
        defaultOpen={false}
        panelContent={
          <OrderSummary 
            checkoutButtonText={checkoutButtonText} 
            handleCheckoutButtonClick={handleCheckoutButtonClick} 
            showItemsCount 
            tabledMode 
            minWidthItemsAmount={80} 
            includeBag={includeBag} 
            setIncludeBag={setIncludeBag}/>
        }
      >
        {({onToggle, isOpen}) => {
          return (
            <Flex
              onClick={() => {
                onToggle()
                setIsOpen((prev) => !prev)
              }}
              cursor="pointer"
              align="center"
              justify="space-between"
              padding="16px"
              width="full"
              borderTop="1px"
              borderBottom="1px"
              borderColor="gray200"
            >
              <Text variant="heading3">Shopping Total</Text>
              <Flex align="center" gap="8px">
                <Text variant="unstyled" style={{fontSize: 18}} weight="medium" as="span">
                  {basket?.currency && (
                    <FormattedNumber
                      style="currency"
                      currency={basket?.currency}
                      value={isMobileFlag ? (basket?.orderTotal ?? 0) : (basket?.productSubTotal ?? 0)}
                    />
                  )}
                </Text>

                <ChevronDownIcon
                  boxSize="16px"
                  style={{transform: isOpen ? 'rotate(180deg)' : undefined}}
                />
              </Flex>
            </Flex>
          )
        }}
      </CollapsablePanel>

      <Stack padding="16px" spacing="16px" width="full">
        {isOpen && !editMode && (
          <OrderSummaryProgressBars border={false} />
        )}
        {showEditOrderMessage && (
          <Box marginBottom="8px">
            <Text variant="text1" color="promotionColorDark" align="center">
              You are editing an existing order, changes will only take effect once you checkout.
            </Text>
          </Box>
        )}
        <BasketToCheckoutButton 
          width="full"
          display="inline-flex"
          checkoutButtonText={checkoutButtonText ?? ''}
          onClick={() => {
            handleCheckoutButtonClick()
          }}
        />
        {editMode && (
        <Button
          variant="outlineDark"
          data-test-selector="discard-changes-button"
          size="sm"
          width="full"
          onClick={() => {
            exitEditModeModal?.onOpen()
          }}
        >
          Discard Changes and Exit
        </Button>
        )}
        {isOpen && !editMode && (
          <BaseButton as={Link} variant="outlineDark" to="/" width="full" size="sm">
            <Text
              variant="unstyled"
              fontSize="inherit"
              fontWeight="inherit"
              data-test-selector="continue-shopping"
              color={{hover: 'gray700', default: 'gray800'}}
            >
              Continue Shopping
            </Text>
          </BaseButton>
        )}
      </Stack>
    </Stack>
  )
})
